import { useContext } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Banner } from 'slab'
import { formatMoney } from 'utils'
import { useGetBrokersMembersMemberIdEnrollmentsCostOfCoverage } from 'utils/web_api'
import { type BrokersMoney } from 'utils/web_api.schemas'

interface MemberCostOfCoverageProps {
  memberId: string
  isDisplayed: boolean
}

const MemberCostOfCoverage = ({
  memberId,
  isDisplayed,
}: MemberCostOfCoverageProps) => {
  const { planYear } = useContext(BrokersMemberContext)
  const { data, isLoading, error } =
    useGetBrokersMembersMemberIdEnrollmentsCostOfCoverage(
      memberId,
      {
        plan_year: planYear || 0,
        start_month: 1,
        end_month: 12,
      },
      {
        query: {
          // Enable when we have valid data
          enabled: isDisplayed && !!planYear
        }
      }
    )

  if (isLoading) {
    return (
      <div className='border rounded-lg shadow p-6'>
        <div className='mb-4'>
          <h3 className='heading-sm'>Monthly Cost of Coverage Summary</h3>
        </div>
        <div
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4'
          data-testid='cost-of-coverage-skeleton'
        >
          <div className='bg-gray-50 rounded p-3'>
            <SkeletonItem
              widthClass='w-20'
              heightClass='h-4'
              data-testid='cost-of-coverage-skeleton-item'
            />
            <SkeletonItem widthClass='w-16' heightClass='h-6 mt-2' />
          </div>
          <div className='bg-gray-50 rounded p-3'>
            <SkeletonItem widthClass='w-20' heightClass='h-4' />
            <SkeletonItem widthClass='w-16' heightClass='h-6 mt-2' />
          </div>
          <div className='bg-gray-50 rounded p-3'>
            <SkeletonItem widthClass='w-20' heightClass='h-4' />
            <SkeletonItem widthClass='w-16' heightClass='h-6 mt-2' />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div data-testid='cost-of-coverage-error'>
        <Banner
          type='critical'
          title='Error loading cost of coverage summary'
        />
      </div>
    )
  }

  if (!data) {
    return null
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  return (
    <div className='border rounded-lg shadow p-6'>
      <div className='mb-4'>
        <h3 className='heading-sm'>Monthly Cost of Coverage Summary</h3>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4'>
        {Object.entries(data.monthly_costs).map(([month, cost]) => {
          const costMoney = cost as BrokersMoney

          if (costMoney.value === '0.0') {
            return null
          }

          return (
            <div key={month} className='bg-gray-50 rounded p-3'>
              <div className='text-sm text-gray-600'>
                {monthNames[parseInt(month) - 1]}
              </div>
              <div className='text-lg font-semibold'>
                {formatMoney(parseFloat(costMoney.value || '0'))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default MemberCostOfCoverage
